/* global Routes */
import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button } from '../../../ui-kit';
import { IconFile } from '../../../ui-kit/icons';
import { getCurrentUser } from '../../../../state/modules/current-user/operations';
import Loader from '../../../general/Loader';
import {
  filterAnnouncementByGroup,
  isLoadingSelector,
  announcementsAtDashboardTime
} from '../../../../state/modules/announcements/selectors';
import { selectDashboardDatetime } from '../../../../state/modules/dashboards/selectors';
import Announcement from './Announcement';
import Settings from './Settings';
import EditAnnouncementModal from './EditAnnouncementModal';
import AccessRightsContext from '../../../contexts/AccessRightsContext';
import moment from 'moment';

const FIFTEEN_MINUTES = 15 * 60 * 1000;

const AnnouncementsWidget = ({ columns, dashboardAPIManager, displayDetails, groupId }) => {
  const [editAnnouncementId, setEditAnnouncementId] = useState(null);

  const dispatch = useDispatch();

  const announcements = useSelector(announcementsAtDashboardTime);
  const announcementsLoading = useSelector(isLoadingSelector);
  const dashboardTime = useSelector(selectDashboardDatetime).dateTime;

  const accessRights = useContext(AccessRightsContext);

  const editAnnouncement = useCallback(announcement => setEditAnnouncementId(announcement.id), []);

  const fetchAnnouncements = () => {
    const params = { time: moment(dashboardTime).format() };
    // replace is needed when an announcement is deleted
    dashboardAPIManager.getAnnouncements({ params, forceFetch: true, replace: true });
  };

  useEffect(() => {
    fetchAnnouncements();
    dispatch(getCurrentUser());
  }, []);

  useEffect(() => {
    // get new announcements every fifteen minutes
    const interval = setInterval(fetchAnnouncements, FIFTEEN_MINUTES);

    // return a function which will be called when the component is torn down.
    // This removes the recurring call to updateInterval above
    return () => clearInterval(interval);
  }, []);

  // get new announcements when props change
  useEffect(fetchAnnouncements, [dashboardTime]);

  const announcementsToDisplay = useMemo(
    () => filterAnnouncementByGroup(announcements, groupId),
    [announcements, groupId]
  );

  const editRights = accessRights?.create?.includes('announcement');

  if (announcementsLoading && announcements.length === 0) return <Loader />;

  if (!editRights && announcementsToDisplay.length === 0) return I18n.t('no_results_found');

  // If there are less announcements than columns, display as many columns
  // as there are announcements
  const actualColumns = Math.min(announcementsToDisplay.length, Number.parseInt(columns || 1));

  return (
    <>
      {editAnnouncementId && (
        <EditAnnouncementModal
          groupId={groupId}
          announcementId={editAnnouncementId}
          onClose={() => setEditAnnouncementId(null)}
          onSuccessfulSave={fetchAnnouncements}
        />
      )}
      {editRights && (
        <Col className="mb-2 d-flex justify-content-end">
          <Button className="me-2" variant="outline-secondary" onClick={() => setEditAnnouncementId('new')}>
            <IconFile /> {I18n.t('communication.announcements.new')}
          </Button>
          <Button
            variant="outline-secondary"
            href={Routes.group_communication_announcements_path(groupId)}
            target="_blank"
          >
            {I18n.t('communication.announcements.manage')}
          </Button>
        </Col>
      )}

      <Row className="announcement-widget g-1" md={actualColumns}>
        {announcementsToDisplay.map(announcement => (
          <Row key={announcement.id} className="g-1">
            <Col>
              <Announcement
                announcement={announcement}
                displayDetails={displayDetails}
                editRights={editRights}
                editAnnouncement={editAnnouncement}
              />
            </Col>
          </Row>
        ))}
      </Row>
    </>
  );
};

Object.assign(AnnouncementsWidget, {
  SettingsContent: Settings,
  defaultSettings: { displayDetails: false, columns: 1 }
});

// way of adding a static SettingsContent variable to a functional component
export default AnnouncementsWidget;
